import 'react-loading-skeleton/dist/skeleton.css';

import {QueryClientProvider} from '@tanstack/react-query';
import ReactDOM from 'react-dom';
import {createRoot} from 'react-dom/client';

import App from 'components/app/App';
import RemoteConfigProvider from 'components/app/RemoteConfigProvider';
import ScrollToTop from 'components/base/ScrollToTop';
import {isDevelopmentMode, StaticConfig} from 'config/staticConfig';
import {queryClient, ReactQueryDevTools} from 'react-query';
import {loadEnsighten} from 'shared/Ensighten/utils';
import HelmetProviderWithAccessibilityAnnouncer from 'shared/PageHelmet/components/HelmetProvider';
import {CustomRouter} from 'shared/Router/components';
import {history} from 'shared/Router/utils';
import {APP_LOCAL_STORAGE_VERSION} from 'shared/Storage/constants/localStorageKeys';
import {clearLocalStorageWithExemptions, clearSessionStorageWithExemptions} from 'shared/Storage/utils';
import {persistTokenFromQueryParam} from 'shared/utils/okta/authQueryParams';
import {setSessionUUID} from 'shared/utils/session';
import GlobalStyles from 'styles/GlobalStyles';
import {loadCypress} from 'utils/cypress';
import StyleSheetManager from 'utils/styled-components/StyleSheetManager';
import {initializeXStateDevTools} from 'xstate-tools';

import 'fonts.css';

const renderApp = () =>
  // eslint-disable-next-line no-restricted-globals
  createRoot(document.getElementById('root') as HTMLElement).render(
    <RemoteConfigProvider>
      <StyleSheetManager>
        <QueryClientProvider client={queryClient}>
          <CustomRouter {...{history}}>
            <HelmetProviderWithAccessibilityAnnouncer>
              <ScrollToTop />
              <GlobalStyles />
              <App />
            </HelmetProviderWithAccessibilityAnnouncer>
          </CustomRouter>
          <ReactQueryDevTools />
        </QueryClientProvider>
      </StyleSheetManager>
    </RemoteConfigProvider>
  );
const version = localStorage.getItem(APP_LOCAL_STORAGE_VERSION);
if (StaticConfig.appVersion && StaticConfig.appVersion !== version) {
  clearLocalStorageWithExemptions();
  clearSessionStorageWithExemptions();

  localStorage.setItem(APP_LOCAL_STORAGE_VERSION, StaticConfig.appVersion);
}

persistTokenFromQueryParam();

setSessionUUID();

if (!StaticConfig.mobileApplicationLoader) {
  loadEnsighten(StaticConfig.ensightenUri);
  loadCypress();
  initializeXStateDevTools();
  renderApp();
  if (isDevelopmentMode) {
    import('react-axe').then((axe) => {
      const react = require('react');
      axe.default(react, ReactDOM, 1000);
    });
  }
} else {
  import('config/mobile').then((mobileConfig) => {
    mobileConfig.bootstrapMobileApp(() => renderApp());
  });
}
